import 'react-image-crop/dist/ReactCrop.css';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type PercentCrop,
  type ReactCropProps,
} from 'react-image-crop';
import { useEffect, useRef, useState } from 'react';
import { getBlobFromCrop } from './utils';
import { CropperHint } from './cropper-hint';
import { Wrapper, HintWrapper } from './styled-components';

type Props = Omit<ReactCropProps, 'children' | 'crop' | 'onChange' | 'onComplete'> & {
  src: string;
  centerCropOnLoad?: boolean;
  onChange?: ReactCropProps['onChange'];
  onComplete?: (...params: [...Parameters<ReactCropProps['onChange']>, Blob]) => void;
  onDrop?: React.DragEventHandler;
  showHint?: boolean;
};

export const Cropper: React.FC<Props> = ({
  src,
  minHeight,
  minWidth,
  maxHeight,
  maxWidth,
  onChange,
  onComplete,
  onDrop,
  aspect,
  showHint = false,
  ...props
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<PercentCrop>();
  const [imageReady, setImageReady] = useState(false);
  const [scale, setScale] = useState<number>(1);

  const handleImageLoad: React.ReactEventHandler<HTMLImageElement> = () => {
    setImageReady(true);

    if (aspect) {
      const { naturalHeight: height, naturalWidth: width } = imageRef.current as HTMLImageElement;

      // setTimeout needed to work correctly with setCrop(undefined) in useEffect so it won't be overridden
      setTimeout(
        () =>
          setCrop(
            centerCrop(
              makeAspectCrop(
                {
                  unit: '%',
                  width: 100,
                },
                aspect,
                width,
                height,
              ),
              width,
              height,
            ),
          ),
        0,
      );
    }
  };

  useEffect(() => {
    if (!imageReady) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const { width, naturalWidth } = entry.target as HTMLImageElement;

      setScale(width / naturalWidth);
    });

    observer.observe(imageRef.current as HTMLImageElement);

    return () => {
      observer.disconnect();
    };
  }, [imageReady]);

  useEffect(() => {
    // Makes crop preview update between images by forcing rerender and ReactCrop onComplete call
    if (src) {
      setCrop(undefined);
    }
  }, [src]);

  return (
    <Wrapper
      onDragOver={(event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDrop={(event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onDrop?.(event);
      }}
      data-testid="cropper"
    >
      <ReactCrop
        minHeight={minHeight ? scale * minHeight : undefined}
        minWidth={minWidth ? scale * minWidth : undefined}
        maxHeight={maxHeight ? scale * maxHeight : undefined}
        maxWidth={maxWidth ? scale * maxWidth : undefined}
        aspect={aspect}
        crop={crop}
        onChange={(pixelCrop, percentageCrop) => {
          onChange?.(pixelCrop, percentageCrop);
          setCrop(percentageCrop);
        }}
        onComplete={async (pixelCrop, percentageCrop) => {
          const blob = await getBlobFromCrop(imageRef.current as HTMLImageElement, percentageCrop);

          onComplete?.(pixelCrop, percentageCrop, blob);
        }}
        {...props}
      >
        <img ref={imageRef} src={src} alt="" onLoad={handleImageLoad} crossOrigin="anonymous" />
      </ReactCrop>
      {showHint && (
        <HintWrapper>
          <CropperHint />
        </HintWrapper>
      )}
    </Wrapper>
  );
};
